import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import { ArrayParam, NumberParam, useQueryParam } from 'use-query-params'
import clsx from 'clsx'
import { Button, Dropdown, Checkbox, Input } from '@pepper/ui'

import { useSelectedCompanyId } from 'utils/functions/misc'
import { useGetWorkflowStatus } from 'utils/hooks/workflows/useGetWorkflowStatus'
import s from './WorkflowStatusFilter.module.css'

export const WorkflowStatusFilter = () => {
  const [open, setOpen] = useState(false)
  const [internalStatuses, setInternalStatuses] = useQueryParam(
    'status',
    ArrayParam
  )
  const [_, setCurrentPage] = useQueryParam('currentPage', NumberParam)
  const [selectedStatusIds, setSelectedStatusIds] = useState<(string | null)[]>(
    []
  )
  const companyId = useSelectedCompanyId()
  const { data: workflowStatuses } = useGetWorkflowStatus({ companyId })
  const [matchingStatuses, setMatchingStatuses] = useState<
    WorkflowStatus[] | undefined
  >([])
  const [inputText, setInputText] = useState('')

  const isChecked = (checkValue: string) => {
    return selectedStatusIds?.includes(checkValue)
  }

  const handleChange = (statusId: string) => {
    if (isChecked(statusId)) {
      setSelectedStatusIds(selectedStatusIds.filter(id => id !== statusId))
    } else {
      setSelectedStatusIds([...selectedStatusIds, statusId])
    }
  }

  const handleApply = () => {
    setInternalStatuses(selectedStatusIds)
    setCurrentPage(0)
    setOpen(false)
  }

  const onVisibleChange = (visible: boolean) => {
    internalStatuses && setSelectedStatusIds(internalStatuses)
    setOpen(visible)
  }

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputText(event.target.value)
    setMatchingStatuses(
      workflowStatuses?.filter(status =>
        status.name.toLowerCase().includes(event.target.value.toLowerCase())
      )
    )
  }

  const handleClose = () => {
    setInputText('')
    setMatchingStatuses(workflowStatuses)
    setInternalStatuses(undefined)
    setSelectedStatusIds([])
    setCurrentPage(0)
    setOpen(false)
  }

  const normalizedStatuses: Record<string, WorkflowStatus> | undefined =
    workflowStatuses?.reduce(
      (prev, curr) => ({
        ...prev,
        [curr.id]: curr,
      }),
      {}
    )

  const appliedFilterText = (): string => {
    if (internalStatuses?.length === 0 || !internalStatuses) {
      return ''
    } else if (internalStatuses?.length === 1 && internalStatuses[0] !== null) {
      return normalizedStatuses?.[internalStatuses[0]].name ?? ''
    } else {
      return `${internalStatuses?.length} selected`
    }
  }

  useEffect(() => {
    setMatchingStatuses(workflowStatuses)
  }, [workflowStatuses])

  useEffect(() => {
    setSelectedStatusIds(internalStatuses ?? [])
  }, [internalStatuses])

  const menu = () => {
    return (
      <div className={s.wrapper}>
        <Input
          onChange={handleInputChange}
          prefix={<Icon icon="lucide:search" className={s.searchIcon} />}
          placeholder="Search status"
          className={s.input}
          value={inputText}
        />
        <div className={s.options}>
          {matchingStatuses?.map(status => (
            <Checkbox
              key={status.id}
              onChange={() => handleChange(status.id)}
              checked={isChecked(status.id)}
              name={status.id}
              className={s.option}
            >
              <div className={s.statusNameColor}>
                <div
                  className={clsx(s.statusColor, {
                    [s.noColor]: status.name === 'No Status',
                  })}
                  style={{
                    backgroundColor: `#${status?.color}`,
                    border: `3px solid #${status?.color}25`,
                  }}
                />
                <div className={s.statusName}>{status.name}</div>
              </div>
            </Checkbox>
          ))}
        </div>
        <div className={s.divider} />
        <div className={s.actions}>
          <Button className={s.actionBtn} type="primary" onClick={handleApply}>
            Apply
          </Button>
          <Button className={s.actionBtn} type="ghost" onClick={handleClose}>
            Reset
          </Button>
        </div>
      </div>
    )
  }

  return (
    <Dropdown
      overlay={menu}
      trigger={['click']}
      visible={open}
      onVisibleChange={onVisibleChange}
    >
      <Button size="small" className={s.button}>
        Internal Status
        <div className={s.selected}>{appliedFilterText()}</div>
        <Icon
          icon="material-symbols:keyboard-arrow-down-rounded"
          width={16}
          height={16}
        />
      </Button>
    </Dropdown>
  )
}
